import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { getErrorMessage } from '@/services/apiClient';
import { formatPhone } from '@/lib/utils';
import {
  FACILITY_SMS_NUMBER_KINDS,
  assignFacilitySmsNumber,
  getAvailableFacilitySmsNumbers,
  getFacilitySmsNumbers,
  removeFacilitySmsNumber,
  type FacilitySmsNumberKind,
  type FacilitySmsNumberSet,
} from '@/services/facilitySmsNumbers.service';

const LABELS: Record<FacilitySmsNumberKind, string> = {
  reply: 'Text reply number',
  confirm: 'Appointment confirmation number',
  marketing: 'Marketing number',
};

const EMPTY_SET: FacilitySmsNumberSet = { reply: null, confirm: null, marketing: null };

type Pending =
  | { action: 'assign'; kind: FacilitySmsNumberKind; number: string }
  | { action: 'remove'; kind: FacilitySmsNumberKind; number: string };

interface FacilityTextNumbersProps {
  facilityId: number;
  facilityName: string;
  /** Show the numbers only, as on a Locked Facilities page. */
  readOnly?: boolean;
}

/**
 * A facility's own text numbers, inside the Edit Facility modal. Each kind
 * either shows the facility's own number or says which practice number it
 * uses instead. Administrators can pick one of the practice's unused numbers
 * or remove the facility's own; everyone else sees the numbers only.
 */
export function FacilityTextNumbers({ facilityId, facilityName, readOnly = false }: FacilityTextNumbersProps) {
  const [own, setOwn] = useState<FacilitySmsNumberSet>(EMPTY_SET);
  const [practice, setPractice] = useState<FacilitySmsNumberSet>(EMPTY_SET);
  const [available, setAvailable] = useState<string[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Why nothing can be changed here when that is expected, not a failure.
  const [notice, setNotice] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<FacilitySmsNumberKind, string>>>({});
  const [pending, setPending] = useState<Pending | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setNotice(null);
    try {
      const overview = await getFacilitySmsNumbers();
      setPractice(overview.practiceNumbers);
      setOwn(overview.facilities.find((f) => f.facilityId === facilityId)?.numbers ?? EMPTY_SET);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Couldn't load this facility's text numbers. Close this window and try again."));
      setLoading(false);
      return;
    }

    try {
      setAvailable(await getAvailableFacilitySmsNumbers());
      setCanManage(true);
    } catch (err) {
      const code = axios.isAxiosError(err)
        ? (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code
        : undefined;
      // Without the list nothing can be changed here: show the numbers only,
      // with the reason (unless it is simply that this user is not an admin).
      // A practice on the shared phone account is the expected case, so its
      // reason is help text; anything else is a real failure.
      setCanManage(false);
      if (code === 'NO_PHONE_ACCOUNT') {
        setNotice(
          getErrorMessage(
            err,
            "Your practice doesn't have its own phone account yet, so facility numbers can't be set up here. Ask Prospera support to set one up for your practice.",
          ),
        );
      } else if (code !== 'NOT_ALLOWED') {
        setLoadError(getErrorMessage(err, "Couldn't load the practice's unused numbers. Try again in a few minutes."));
      }
      setAvailable([]);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmPending() {
    if (!pending) return;
    const { action, kind, number } = pending;
    setErrors((e) => ({ ...e, [kind]: undefined }));
    try {
      if (action === 'assign') {
        await assignFacilitySmsNumber(facilityId, kind, number);
      } else {
        await removeFacilitySmsNumber(facilityId, kind);
      }
      await load();
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [kind]: getErrorMessage(err, "That didn't work, and nothing was changed. Try again in a few minutes."),
      }));
    }
  }

  function practiceText(kind: FacilitySmsNumberKind): string {
    const number = practice[kind];
    return number ? `Uses the practice number ${formatPhone(number)}` : 'Uses the practice number';
  }

  function confirmMessage(p: Pending): string {
    const label = LABELS[p.kind].toLowerCase();
    if (p.action === 'assign') {
      return `${formatPhone(p.number)} will become ${facilityName}'s ${label}. Patients of this facility will get these texts from it, and their replies will reach your practice. Texts only go through if this number is registered for texting on your practice's account; if you're not sure it is, ask Prospera support before using it.`;
    }
    const fallback = practice[p.kind] ? ` ${formatPhone(practice[p.kind])}` : '';
    return `${facilityName} will go back to the practice number${fallback} for these texts. Texts sent to ${formatPhone(p.number)} after this will no longer reach your practice.`;
  }

  // The Edit Facility modal's collapsible "Text Numbers" section titles it.
  return (
    <div className="space-y-4">
      {loadError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{loadError}</p>
      )}
      {notice && <p className="text-sm text-slate-500">{notice}</p>}

      {FACILITY_SMS_NUMBER_KINDS.map((kind) => {
        const number = own[kind];
        return (
          <FormField key={kind} label={LABELS[kind]} error={errors[kind]}>
            {number ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-slate-900">{formatPhone(number)}</span>
                {canManage && !readOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPending({ action: 'remove', kind, number })}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-slate-500">{loading ? 'Loading…' : practiceText(kind)}</p>
                {canManage && !readOnly && !loading && (
                  available.length > 0 ? (
                    <Select
                      placeholder="Choose a number for this facility"
                      options={available.map((n) => ({ value: n, label: formatPhone(n) }))}
                      value={null}
                      onChange={(val) => {
                        if (val) setPending({ action: 'assign', kind, number: val });
                      }}
                    />
                  ) : (
                    <p className="text-xs text-slate-400">
                      No unused numbers. Ask Prospera support to add one to your practice.
                    </p>
                  )
                )}
              </div>
            )}
          </FormField>
        );
      })}

      <ConfirmDialog
        isOpen={pending !== null}
        title={pending?.action === 'remove' ? 'Remove this number?' : 'Use this number?'}
        message={pending ? confirmMessage(pending) : ''}
        confirmLabel={pending?.action === 'remove' ? 'Remove' : 'Use number'}
        variant={pending?.action === 'remove' ? 'danger' : 'warning'}
        onConfirm={confirmPending}
        onClose={() => setPending(null)}
      />
    </div>
  );
}
